import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { BACKEND, fmtDate } from "@/lib/api";
import { Wordmark } from "@/components/Wordmark";
import { FactStatus, Spinner } from "@/components/common";
import { LiveRecord } from "@/pages/Live";
import { CountryBlock } from "@/components/Perspective";
import { VerifyReminder } from "@/components/VerifyReminder";
import { SharedClaim } from "@/components/SharedClaim";
import { SharedCard } from "@/components/SharedCard";
import { MinorDifferences } from "@/components/MinorDifferences";

// Public, logged-out share page for a Fact or a Middle Live record (external sharing growth loop).
export default function SharePage() {
  const { kind, itemId } = useParams();
  const [params] = useSearchParams();
  const [d, setD] = useState(null);
  useEffect(() => {
    const url = kind === "perspective" ? `perspective/${itemId}?c=${encodeURIComponent(params.get("c") || "")}` : `${kind === "live" ? "live" : kind === "claim" ? "claims" : kind === "card" ? "cards" : "facts"}/${itemId}`;
    fetch(`${BACKEND}/api/public/${url}`).then((r) => (r.ok ? r.json() : false)).then(setD).catch(() => setD(false));
    fetch(`${BACKEND}/api/public/share-visit`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ kind, item_id: itemId, ref: params.get("ref") || "" }) }).catch(() => {});
  }, [kind, itemId]); // eslint-disable-line
  if (d === null) return <Spinner />;
  const b = d?.brand || {};
  const f = d?.fact;
  const pc = d?.country;
  return (
    <div className="min-h-screen" data-testid="share-page">
      <header className={`mx-auto flex h-20 ${pc ? "max-w-6xl" : "max-w-3xl"} items-center justify-between px-5`}><Wordmark />
        <Link to="/sign-up" data-testid="share-join-btn" className="rounded-full bg-slate-900 px-5 py-2 text-sm font-medium text-white">Join Middle</Link></header>
      <main className={`mx-auto ${pc ? "max-w-6xl" : "max-w-3xl"} space-y-6 px-5 pb-20`}>
        <div className="rounded-3xl bg-[#13352C] p-6 text-white sm:p-8"><p className="font-serif text-3xl leading-tight sm:text-4xl" data-testid="share-campaign">{b.facts_campaign}</p><p className="mt-2 text-sm text-emerald-100/80">{b.facts_positioning}</p></div>
        {!d ? <p className="text-slate-500">This item is not available.</p> : d.card ? <SharedCard d={d} /> : d.claim ? <SharedClaim c={d.claim} /> : pc ? (
          <section className="space-y-4" data-testid="share-perspective">
            <p className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--facts))]">Middle's perspective</p>
            <h1 className="text-3xl font-medium leading-tight">{d.live.title}</h1>
            <CountryBlock c={pc} />
            <p className="text-[11px] text-slate-400">Written by AI from Middle's verified record and widely reported party positions. Updated {fmtDate(d.generated_at)}.</p>
          </section>
        ) : f ? (
          <article className="surface space-y-4 p-6" data-testid="share-fact">
            <div className="flex flex-wrap items-center gap-2"><FactStatus status={f.status} /><span className="text-xs text-slate-500">{f.category}</span></div>
            <h1 className="text-3xl font-medium leading-tight">{f.headline}</h1><p className="text-lg text-slate-700">{f.summary}</p>
            <p className="text-sm text-slate-600">{f.explanation}</p>
            <MinorDifferences items={f.minor_differences} status={f.status} testid="share-fact-minor-differences" />
            <a href={f.original?.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between rounded-xl border p-3 text-sm hover:border-slate-400"><span><b>{f.source_name}</b> · original source</span><ExternalLink size={15} /></a>
            <p className="text-xs text-slate-500">Checked {fmtDate(f.checked_at)} · {f.corroborating?.length || 0} corroborating · {f.conflicting?.length || 0} conflicting approved sources{f.revisions?.length ? ` · corrected ${f.revisions.length}×` : ""}</p>
          </article>
        ) : (<><h1 className="text-3xl font-medium">{d.live.title}</h1><p className="text-slate-600">{d.live.summary}</p><LiveRecord ev={d.live} publicView /></>)}
        <div className="surface flex flex-wrap items-center justify-between gap-3 p-5"><p className="font-serif text-xl">{b.facts_primary}</p>
          <Link to="/sign-up" className="rounded-full bg-[hsl(var(--facts))] px-5 py-2.5 text-sm font-medium text-white" data-testid="share-cta">Get the facts on Middle</Link></div>
      <VerifyReminder />
      </main>
    </div>
  );
}
